import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  User, 
  Phone, 
  Mail, 
  Plus,
  AlertCircle,
  Users
} from 'lucide-react';
import { toast } from 'react-toastify';
import contactRequests from '../../api/Customer/ContactRequests';

const ContactAddModal = ({ 
  isOpen, 
  onClose, 
  groupName,
  groupSlug,
  onSuccess 
}) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone_number: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }

    if (!formData.phone_number.trim()) {
      newErrors.phone_number = 'Phone number is required';
    } else {
      // Remove spaces and validate
      const cleanPhone = formData.phone_number.replace(/\s/g, '').replace(/\D/g, '');
      if (cleanPhone.length !== 10 || !/^[7-9][0-1][0-9]{8}$/.test(cleanPhone)) {
        newErrors.phone_number = 'Phone number must be exactly 10 digits starting with 7, 8, or 9';
      }
    }

    // Email validation (optional but if provided, must be valid)
    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    if (field === 'phone_number') {
      // Remove all non-digits and limit to 10 digits
      const cleanValue = value.replace(/\D/g, '').slice(0, 10);
      setFormData(prev => ({ ...prev, [field]: cleanValue }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      // Clean phone number and add 234 prefix
      const cleanPhone = formData.phone_number.replace(/\s/g, '').replace(/\D/g, '');
      const phoneWithPrefix = '233' + cleanPhone;
      
      const contactData = {
        phone_number: phoneWithPrefix,
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        email: formData.email.trim() || null
      };

      await contactRequests.addContact(groupSlug, contactData);
      
      toast.success('Contact added successfully!');
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone_number: ''
      });
      setErrors({});
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error adding contact:', error);
      toast.error(error.response?.data?.message || 'Failed to add contact');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone_number: ''
    });
    setErrors({});
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="flex fixed inset-0 z-50 justify-center items-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 backdrop-blur-sm bg-black/60"
            onClick={handleClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative bg-white rounded-3xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col"
          >
            {/* Header - Fixed */}
            <div className="relative flex-shrink-0 p-8 text-white bg-gradient-to-r from-blue-600 to-indigo-700 rounded-t-3xl">
              <div className="flex justify-between items-center">
                <div className="flex gap-4 items-center">
                  <div className="flex justify-center items-center w-16 h-16 rounded-2xl backdrop-blur-sm bg-white/20">
                    <Users className="w-8 h-8" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">Add New Contact</h2>
                    <p className="mt-1 text-blue-100">Add to {groupName} group</p>
                  </div>
                </div>
                <button
                  onClick={handleClose}
                  className="p-3 rounded-xl transition-all duration-200 text-white/80 hover:text-white hover:bg-white/10"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              {/* Decorative elements */}
              <div className="absolute top-0 right-0 w-32 h-32 rounded-full translate-x-16 -translate-y-16 bg-white/5"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 rounded-full -translate-x-12 translate-y-12 bg-white/5"></div>
            </div>

            {/* Form - Scrollable */}
            <div className="overflow-y-auto flex-1">
              <div className="p-8 pb-12">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    {/* First Name */}
                    <div>
                      <label className="block mb-3 text-sm font-semibold text-gray-700">
                        First Name *
                      </label>
                      <div className="relative">
                        <User className="absolute left-4 top-1/2 w-5 h-5 text-gray-400 transform -translate-y-1/2" />
                        <input
                          type="text"
                          value={formData.first_name}
                          onChange={(e) => handleInputChange('first_name', e.target.value)}
                          className={`w-full pl-12 pr-4 py-3 border-2 rounded-xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 text-base ${
                            errors.first_name ? 'border-red-300 bg-red-50' : 'border-gray-200 hover:border-gray-300'
                          }`}
                          placeholder="Enter first name"
                          disabled={isSubmitting}
                        />
                      </div>
                      {errors.first_name && (
                        <p className="flex gap-2 items-center mt-2 text-sm text-red-600">
                          <AlertCircle className="w-4 h-4" />
                          {errors.first_name}
                        </p>
                      )}
                    </div>

                    {/* Last Name */}
                    <div>
                      <label className="block mb-3 text-sm font-semibold text-gray-700">
                        Last Name *
                      </label>
                      <div className="relative">
                        <User className="absolute left-4 top-1/2 w-5 h-5 text-gray-400 transform -translate-y-1/2" />
                        <input
                          type="text"
                          value={formData.last_name}
                          onChange={(e) => handleInputChange('last_name', e.target.value)}
                          className={`w-full pl-12 pr-4 py-3 border-2 rounded-xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 text-base ${
                            errors.last_name ? 'border-red-300 bg-red-50' : 'border-gray-200 hover:border-gray-300'
                          }`}
                          placeholder="Enter last name"
                          disabled={isSubmitting}
                        />
                      </div>
                      {errors.last_name && (
                        <p className="flex gap-2 items-center mt-2 text-sm text-red-600">
                          <AlertCircle className="w-4 h-4" />
                          {errors.last_name}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Phone Number */}
                  <div>
                    <label className="block mb-3 text-sm font-semibold text-gray-700">
                      Phone Number *
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-4 top-1/2 w-5 h-5 text-gray-400 transform -translate-y-1/2" />
                      <input
                        type="tel"
                        max={10}
                        value={formData.phone_number}
                        onChange={(e) => handleInputChange('phone_number', e.target.value)}
                        className={`w-full pl-12 pr-4 py-3 border-2 rounded-xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 text-base font-mono ${
                          errors.phone_number ? 'border-red-300 bg-red-50' : 'border-gray-200 hover:border-gray-300'
                        }`}
                        placeholder="8012345678"
                        disabled={isSubmitting}
                      />
                    </div>
                    {errors.phone_number && (
                      <p className="flex gap-2 items-center mt-2 text-sm text-red-600">
                        <AlertCircle className="w-4 h-4" />
                        {errors.phone_number}
                      </p>
                    )}
                    <p className="flex gap-2 items-center mt-2 text-sm text-gray-500">
                      <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                      Enter 10 digits only. System will add +234 prefix automatically.
                    </p>
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block mb-3 text-sm font-semibold text-gray-700">
                      Email Address <span className="font-normal text-gray-400">(Optional)</span>
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 w-5 h-5 text-gray-400 transform -translate-y-1/2" />
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        className={`w-full pl-12 pr-4 py-3 border-2 rounded-xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 text-base ${
                          errors.email ? 'border-red-300 bg-red-50' : 'border-gray-200 hover:border-gray-300'
                        }`}
                        placeholder="Enter email address"
                        disabled={isSubmitting}
                      />
                    </div>
                    {errors.email && (
                      <p className="flex gap-2 items-center mt-2 text-sm text-red-600">
                        <AlertCircle className="w-4 h-4" />
                        {errors.email}
                      </p>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-4 pt-8 pb-4">
                    <button
                      type="button"
                      onClick={handleClose}
                      className="flex-1 px-6 py-3 text-base font-semibold text-gray-700 rounded-xl border-2 border-gray-300 transition-all duration-200 hover:bg-gray-50 hover:border-gray-400"
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="flex-1 px-6 py-3 text-base font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl shadow-lg transition-all duration-200 hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-xl"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <div className="flex gap-3 justify-center items-center">
                          <div className="w-5 h-5 rounded-full border-2 border-white animate-spin border-t-transparent" />
                          Adding Contact...
                        </div>
                      ) : (
                        <div className="flex gap-2 justify-center items-center">
                          <Plus className="w-5 h-5" />
                          Add Contact
                        </div>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ContactAddModal; 